import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { pdfStyles } from '../../..';
import { InvoiceInterface } from '../../../../interfaces/invoice.interface';
import { getVivaAuthenticationCode, getVivaLicenseKey, getVivaUniqueDocId } from '../../../../services/viva/viva.utils';

interface Props {
  doc: InvoiceInterface;
}

// The QR itself lives in `PdfQrCode.tsx` now, rendered next to `PdfNotes` instead of here — this
// component still covers the rest of the AADE/provider data (UID, MARK, provider signature info)
// and the Publisher/Receiver signature-line labels, both still shown in the page-edge footer.
export const PdfAadeData = ({ doc }: Props) => {
  const { t } = useTranslation();
  const signatures = useMemo(() => doc?.providerData?.fiscalisationDetails?.ftSignatures || [], [doc]);

  const getProviderAuthCode = useCallback(() => {
    const providerAuthCode = getVivaAuthenticationCode(signatures);
    if (providerAuthCode) {
      return (
        <View style={[styles.row, { paddingBottom: 2 }]}>
          <Text style={[pdfStyles.smallestTitleRegular]}>{providerAuthCode}</Text>
          <Text style={[pdfStyles.smallestTitle, { paddingLeft: 1 }]}>({t('PDF.PROVIDER_AUTH_CODE')})</Text>
        </View>
      );
    }
  }, [signatures, t]);

  const getProviderLicenseKey = useCallback(() => {
    const providerLicenseKey = getVivaLicenseKey(signatures);
    if (providerLicenseKey) {
      return (
        <View style={[styles.row, { paddingBottom: 2 }]}>
          <Text style={[pdfStyles.smallestTitleRegular]}>{providerLicenseKey}</Text>
        </View>
      );
    }
  }, [signatures]);

  const getProviderDocId = useCallback(() => {
    const providerDocId = getVivaUniqueDocId(signatures);
    if (providerDocId) {
      return (
        <View style={[styles.row, { paddingBottom: 2 }]}>
          <Text style={[pdfStyles.smallestTitleRegular]}>{providerDocId}</Text>
        </View>
      );
    }
  }, [signatures]);

  const getAadeData = useCallback(() => {
    if (doc?.sentToAade) {
      return (
        <View style={pdfStyles.flexStartCenterRow}>
          <View style={pdfStyles.column}>
            <View style={[styles.row, { paddingBottom: 2 }]}>
              <Text style={[pdfStyles.smallestTitleRegular]}>{doc?.aadeUID}</Text>
              <Text style={[pdfStyles.smallestTitle, { paddingLeft: 1 }]}>({t('PDF.AADE_ID')})</Text>
            </View>
            <View style={[styles.row, { paddingBottom: 2 }]}>
              <Text style={[pdfStyles.smallestTitleRegular]}>{doc?.mark}</Text>
              <Text style={[pdfStyles.smallestTitle, { paddingLeft: 1 }]}>({t('PDF.AADE_MARK')})</Text>
            </View>
            {getProviderAuthCode()}
            {getProviderLicenseKey()}
            {getProviderDocId()}
          </View>
        </View>
      );
    }
  }, [doc?.aadeUID, doc?.mark, doc?.sentToAade, getProviderAuthCode, getProviderLicenseKey, getProviderDocId, t]);

  const getSection = useCallback(() => {
    return (
      <View style={[styles.center, { borderWidth: 0.00001 }]}>
        <View style={styles.minWidthAade}>{getAadeData()}</View>
        <View style={[pdfStyles.row, { paddingRight: 60, paddingBottom: 20, paddingLeft: 40 }]}>
          <View style={{ paddingRight: 100 }}>
            <Text style={[pdfStyles.smallTitle, styles.placeholder]}>{t('PDF.PUBLISHER')}</Text>
          </View>
          <View>
            <Text style={[pdfStyles.smallTitle, styles.placeholder]}>{t('PDF.RECEIVER')}</Text>
          </View>
        </View>
      </View>
    );
  }, [getAadeData, t]);

  return getSection();
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  center: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  smallPlaceholder: {
    width: 28,
  },
  placeholder: {
    width: 60,
  },
  minWidthAade: {
    minWidth: 260,
  },
});
